
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEpisodes } from '../../hooks/useEpisodes';
import { useAuth } from '../../hooks/useAuth';
import { useIndustries } from '../../hooks/useIndustries';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function EpisodeLandingPage() {
  const { user, refreshSubscriberStatus } = useAuth();
  const { industries } = useIndustries();
  const { episodes, loading: episodesLoading } = useEpisodes();
  const navigate = useNavigate();

  // Find the featured episode (Episode 1) or the first episode
  const featuredEpisode = episodes?.find(ep => ep.id === 1) || episodes?.[0];

  // Registration form state
  const [step, setStep] = useState<'form' | 'verification'>('form');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    industry: '',
    agreeToTerms: false
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [devCode, setDevCode] = useState<string>('');

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands',
    'Sweden', 'Norway', 'Denmark', 'Finland', 'Australia', 'New Zealand', 'Japan', 'South Korea',
    'Singapore', 'Hong Kong', 'Switzerland', 'Austria', 'Belgium', 'Ireland', 'Portugal', 'Other'
  ];

  const handleRegistrationSuccess = (newUser: any) => {
    console.log('Registration successful:', newUser);
    // Refresh user state
    if (refreshSubscriberStatus) {
      refreshSubscriberStatus();
    }
    // Redirect to episodes page after successful registration
    navigate('/episodes');
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.country) return;

    setIsLoading(true);
    setError('');
    setDevCode('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase configuration is missing. Please check environment variables.');
      }

      const requestBody = {
        email: formData.email.trim(),
        type: 'registration',
        name: formData.name.trim(),
        country: formData.country,
        industry: formData.industry
      };

      const response = await fetch(`${supabaseUrl}/functions/v1/send-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify(requestBody),
      });

      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      setExpiresAt(data.expires_at);

      if (data.dev_mode && data.test_code) {
        setDevCode(data.test_code);
        setSuccessMessage(`Development Mode: Your verification code is ${data.test_code}. Also check your email and spam folder.`);
      } else {
        setSuccessMessage('Verification code sent to your email! Please check your inbox and spam folder.');
      }

      setStep('verification');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim() || verificationCode.length !== 6) return;

    setIsLoading(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          code: verificationCode.trim(),
          type: 'registration',
          name: formData.name.trim(),
          country: formData.country,
          industry: formData.industry
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      // Refresh the user state
      if (refreshSubscriberStatus) {
        await refreshSubscriberStatus();
      }

      // Reset form
      setStep('form');
      setFormData({ name: '', email: '', country: '', industry: '', agreeToTerms: false });
      setVerificationCode('');
      setError('');
      setSuccessMessage('');
      setExpiresAt('');
      setDevCode('');

      // Redirect to episodes page after successful registration
      navigate('/episodes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const formatExpiry = (expiryString: string) => {
    if (!expiryString) return '';
    const expiry = new Date(expiryString);
    return expiry.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const shareUrl = `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episode-landing`;
  const shareTitle = featuredEpisode ? featuredEpisode.title : 'DBD Episode 1: Guillermo Erickson Sauza - Tequila Fortaleza';
  const shareDescription = featuredEpisode ? featuredEpisode.description : 'THE Distilled Business Dialogues podcast - Get new episodes in advance with insights from spirits industry legends';

  // Use episode image or fallback
  const backgroundImage = featuredEpisode?.advertisingImage || 'https://i.postimg.cc/MG3qc2QS/Screenshot-2025-09-15-100021.png';

  // SEO Schema.org data
  const landingPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `DBD ${featuredEpisode ? featuredEpisode.title : 'Episode 1: Guillermo Erickson Sauza - Tequila Fortaleza'} Landing Page`,
    "description": shareDescription,
    "url": shareUrl,
    "mainEntity": {
      "@type": "PodcastEpisode",
      "name": shareTitle,
      "description": featuredEpisode ? featuredEpisode.description : "Deep conversation with Guillermo Erickson Sauza about Tequila Fortaleza and the spirits industry",
      "partOfSeries": {
        "@type": "PodcastSeries",
        "name": "DBD Podcast",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "DBD Podcast",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
    }
  };

  // Show loading while episodes are being fetched
  if (episodesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line animate-spin text-white text-2xl"></i>
          </div>
          <p className="text-gray-600">Loading episode...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEOHead
        title={`${shareTitle} | Distilled Business Dialogues`}
        description={shareDescription}
        keywords="tequila fortaleza, guillermo erickson sauza, spirits industry, business podcast, distilled business dialogues, aliassmith, mezcal importer, premium tequila"
        ogTitle={shareTitle}
        ogDescription={shareDescription}
        ogImage={backgroundImage}
        ogType="website"
        canonical={shareUrl}
      />

      <SchemaOrg data={landingPageSchema} />

      {/* Full-page Hero Section with Episode Background */}
      <section
        className="relative min-h-screen bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2)), url('${backgroundImage}')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/50 to-white/30"></div>

        <div className="relative z-10 container mx-auto px-6 py-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Perfect alignment grid - both boxes at exactly the same level and same width */}
            <div className="flex justify-center items-start gap-8 mb-16">
              
              {/* Left Content - Episode Card - 60% width */}
              <div className="w-[30%] flex-shrink-0">
                {/* Logo with dark background */}
                <div className="mb-8">
                  <div className="mb-6 inline-block">
                    <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4">
                      <img
                        src="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/5910930edf8f8435ab6c0a56926fd219.png"
                        alt="DBD Podcast Logo"
                        className="h-32 w-auto"
                      />
                    </div>
                  </div>
                  <div className="w-24 h-1 bg-amber-500 mb-6"></div>
                </div>

                {/* Episode Card - Same style as home page with dynamic data */}
                <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden group">
                  <div
                    className="h-48 bg-cover bg-center"
                    style={{
                      backgroundImage: `url('${backgroundImage}')`
                    }}
                  ></div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>{featuredEpisode ? new Date(featuredEpisode.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'September 2025'}</span>
                      <span className="mx-2">•</span>
                      <span>{featuredEpisode?.duration || '1 hour 13 min'}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                      {featuredEpisode ? `DBD Episode ${featuredEpisode.id}: ${featuredEpisode.title}` : 'DBD Episode 1: Guillermo Erickson Sauza - Tequila Fortaleza'}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-4 text-sm">
                      {featuredEpisode?.description || 'In this engaging conversation, Claes interviews the head of legendary Tequila brand Fortaleza. They discuss Guillermo\'s journey, the evolution of his brand and the strategies that led to its success. Guillermo shares insights on understanding market dynamics, the importance of customer relationships, and the lessons learned from mistakes. He emphasizes the significance of thorough research and the need for a solid business plan. The conversation also touches on the future of the industry.'}
                    </p>
                    <p className="text-sm text-amber-600 font-medium mb-4">
                      Guest: {featuredEpisode?.guest || 'Guillermo Erickson Sauza'}
                      {featuredEpisode?.guest2 && (
                        <span>, {featuredEpisode.guest2}</span>
                      )}
                      {featuredEpisode?.guest3 && (
                        <span>, {featuredEpisode.guest3}</span>
                      )}
                      {featuredEpisode?.guest4 && (
                        <span>, {featuredEpisode.guest4}</span>
                      )}
                      {featuredEpisode?.guest5 && (
                        <span>, {featuredEpisode.guest5}</span>
                      )}
                    </p>
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => navigate(`/episodes/${featuredEpisode?.id || 1}`)}
                        className="text-gray-600 hover:text-amber-600 transition-colors cursor-pointer"
                      >
                        Show Notes
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Content - Subscription Form - 60% width and perfectly aligned with matching height */}
              <div className="w-[30%] flex-shrink-0 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 flex flex-col justify-between min-h-[600px]">
                {user ? (
                  // If user is already logged in
                  <div className="text-center space-y-4 flex flex-col justify-center flex-1">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <i className="ri-check-circle-line text-green-500 text-2xl mb-2"></i>
                      <h4 className="text-lg font-bold text-green-800 mb-1">Welcome back, {user.user_metadata?.name || user.email}!</h4>
                      <p className="text-green-700 text-sm">You already have access to all episodes including this featured conversation with Guillermo Erickson Sauza.</p>
                    </div>

                    <button
                      onClick={() => navigate('/episodes')}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                    >
                      Listen to Episode 1 Now
                    </button>
                  </div>
                ) : (
                  // Subscription Form
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <div className="text-center mb-4">
                        <h3 className="text-2xl font-bold text-gray-800 mb-1">Subscribe to the DBD Podcast</h3>
                        <p className="text-gray-600 text-sm">Get exclusive early access to episodes and spirits industry insights</p>
                      </div>

                      {step === 'form' && (
                        <form onSubmit={handleSubmitForm} className="space-y-4">
                          <div>
                            <input
                              type="text"
                              id="name"
                              value={formData.name}
                              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                              required
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-sm"
                              placeholder="Full Name *"
                            />
                          </div>

                          <div>
                            <input
                              type="email"
                              id="email"
                              value={formData.email}
                              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                              required
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-sm"
                              placeholder="Email Address *"
                            />
                          </div>

                          <div>
                            <div className="relative">
                              <select
                                id="country"
                                value={formData.country}
                                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                                required
                                className="w-full px-4 py-2.5 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">Select your country *</option>
                                {countries.map((country) => (
                                  <option key={country} value={country}>{country}</option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <i className="ri-arrow-down-s-line text-gray-400"></i>
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className="relative">
                              <select
                                id="industry"
                                value={formData.industry}
                                onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                                className="w-full px-4 py-2.5 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">Industry (optional)</option>
                                {industries.map((industry) => (
                                  <option key={industry.id} value={industry.name}>{industry.name}</option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <i className="ri-arrow-down-s-line text-gray-400"></i>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              id="terms"
                              checked={formData.agreeToTerms}
                              onChange={(e) => setFormData(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                              className="mt-1 h-3 w-3 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                            />
                            <label htmlFor="terms" className="text-xs text-gray-600 cursor-pointer">
                              I agree to receive emails about new episodes, exclusive content, and updates.
                              I understand I can unsubscribe at any time.
                            </label>
                          </div>

                          {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="flex items-center">
                                <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
                                <p className="text-red-700 text-sm">{error}</p>
                              </div>
                            </div>
                          )}

                          <button
                            type="submit"
                            disabled={isLoading || !formData.name.trim() || !formData.email.trim() || !formData.country || !formData.agreeToTerms}
                            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                          >
                            {isLoading ? (
                              <span className="flex items-center justify-center">
                                <i className="ri-loader-4-line animate-spin mr-2"></i>
                                Sending Code...
                              </span>
                            ) : (
                              'Get Exclusive Access'
                            )}
                          </button>
                        </form>
                      )}

                      {step === 'verification' && (
                        <form onSubmit={handleVerifyCode} className="space-y-4">
                          <div className="text-center mb-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <i className="ri-mail-check-line text-green-600 text-xl"></i>
                            </div>
                            <h4 className="text-lg font-bold text-gray-800 mb-1">Check Your Email</h4>
                            <p className="text-gray-600 text-sm">
                              We've sent a 6-digit verification code to<br />
                              <strong>{formData.email}</strong>
                            </p>
                          </div>

                          {successMessage && (
                            <div className={`border rounded-lg p-3 ${devCode ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                              <div className="flex items-start">
                                <i className={`text-lg mr-2 mt-0.5 ${devCode ? 'ri-code-line text-blue-500' : 'ri-information-line text-green-500'}`}></i>
                                <div>
                                  <p className={`text-sm ${devCode ? 'text-blue-700' : 'text-green-700'}`}>{successMessage}</p>
                                  {expiresAt && (
                                    <p className={`text-xs mt-2 ${devCode ? 'text-blue-600' : 'text-green-600'}`}>
                                      Code expires at {formatExpiry(expiresAt)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div>
                            <input
                              type="text"
                              id="verification-code"
                              value={verificationCode}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setVerificationCode(value);
                              }}
                              required
                              maxLength={6}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-center text-xl font-mono tracking-widest"
                              placeholder="000000"
                            />
                            <p className="text-sm text-gray-500 mt-2 text-center">
                              Enter the 6-digit code from your email{devCode && ' or use the code shown above'}
                            </p>
                          </div>

                          {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="flex items-center">
                                <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
                                <p className="text-red-700 text-sm">{error}</p>
                              </div>
                            </div>
                          )}

                          <div className="space-y-2">
                            <button
                              type="submit"
                              disabled={isLoading || verificationCode.length !== 6}
                              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                            >
                              {isLoading ? (
                                <span className="flex items-center justify-center">
                                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                                  Verifying...
                                </span>
                              ) : (
                                'Complete Registration'
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => setStep('form')}
                              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-arrow-left-line mr-2"></i>
                              Back to Form
                            </button>
                          </div>

                          <div className="text-center">
                            <button
                              type="button"
                              onClick={handleSubmitForm}
                              disabled={isLoading}
                              className="text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors cursor-pointer"
                            >
                              Didn't receive the code? Resend
                            </button>
                          </div>
                        </form>
                      )}
                    </div>

                    {/* Bottom section with benefits - always at bottom */}
                    <div className="bg-gray-50 rounded-lg p-3 mt-4">
                      <div className="flex items-start space-x-2">
                        <i className="ri-gift-line text-amber-500 text-base mt-0.5"></i>
                        <div className="text-xs text-gray-600">
                          <p className="font-medium mb-1">What you'll get:</p>
                          <p>• Early access to Episode 1 with Guillermo Erickson Sauza</p>
                          <p>• Exclusive spirits industry insights</p>
                          <p>• Complete show notes and transcripts</p>
                          <p>• Future episodes delivered in advance</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Content below boxes - Centered and spanning both box widths */}
            <div className="mt-16 max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight text-center">
                <span className="text-amber-500">THE</span> Distilled Business Dialogues podcast
              </h1>

              <div className="space-y-6 text-lg md:text-xl leading-relaxed mb-8 text-center">
                <p className="text-gray-700">
                  With this free subscription, you will receive new episodes in advance where we distil the best advice
                  from spirits industry legends to help you take your business to the next level.
                </p>

                <div className="border-l-4 border-amber-500 pl-6 bg-white/60 backdrop-blur-sm p-6 rounded-r-lg text-left max-w-3xl mx-auto">
                  <p className="font-semibold text-amber-600 mb-4">Our Mission</p>
                  <p className="text-gray-700">
                    DBD Podcast explores the intersection of business psychology, entrepreneurship, and industry expertise. 
                    We believe that the best business insights come from deep, thoughtful conversations with leaders who've 
                    navigated complex challenges.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Each episode distills complex business concepts into actionable wisdom, creating a rich dialogue 
                    that you can apply immediately to your own ventures and leadership journey.
                  </p>
                </div>
              </div>

              {/* Social Sharing - Centered below */}
              <div className="border-t border-amber-500/30 pt-8 text-center">
                <h4 className="text-lg font-semibold mb-4 text-amber-600">Share This Episode</h4>
                <div className="flex justify-center space-x-4">
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle + ' - ' + shareDescription)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-amber-500/20 hover:bg-amber-500 border border-amber-400/30 hover:border-amber-400 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer group"
                  >
                    <i className="ri-twitter-x-line text-amber-600 group-hover:text-white text-xl"></i>
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-amber-500/20 hover:bg-amber-500 border border-amber-400/30 hover:border-amber-400 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer group"
                  >
                    <i className="ri-facebook-fill text-amber-600 group-hover:text-white text-xl"></i>
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-amber-500/20 hover:bg-amber-500 border border-amber-400/30 hover:border-amber-400 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer group"
                  >
                    <i className="ri-linkedin-fill text-amber-600 group-hover:text-white text-xl"></i>
                  </a>
                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: shareTitle,
                          text: shareDescription,
                          url: shareUrl
                        });
                      } else {
                        navigator.clipboard.writeText(shareUrl);
                        alert('Link copied to clipboard!');
                      }
                    }}
                    className="w-12 h-12 bg-amber-500/20 hover:bg-amber-500 border border-amber-400/30 hover:border-amber-400 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer group"
                  >
                    <i className="ri-share-line text-amber-600 group-hover:text-white text-xl"></i>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

      </section>

    </div>
  );
}
